import {
    ClaimApi,
    CoreFetch,
    ProfessionalApi,
    ProviderClaim,
} from '@enter/core';
import type {
    IClaimStore,
    IDialogContext,
    IPatientStore,
    IStatementDetailsProps,
} from './statement-details.types';

export type ListEqualsFilter = (term?: string | string[]) => string[] | undefined;

export const buildClaimRowHref = (accountId: string | undefined, claimId?: string): string => {
    const id = claimId || '';
    return accountId ? `/${accountId}/claims/${id}` : `/claims/${id}`;
};

export type StatementAdapterConfig = {
    equalsFilter: ListEqualsFilter;
    loadPatientStatement: IPatientStore['loadPatientStatement'];
    claimStore: IClaimStore;
    dialogContext: IDialogContext;
    listClaimPayments: NonNullable<IStatementDetailsProps['fetchClaimPayments']>;
};

export type StatementAdapter = Pick<
    IStatementDetailsProps,
    | 'patientStore'
    | 'claimStore'
    | 'dialogContext'
    | 'fetchProfessionalClaims'
    | 'fetchClaimPayments'
    | 'fetchClaimsByPatientId'
    | 'equalsFilter'
>;

/**
 * Builds the shared StatementDetailsPage adapter surface for Support / Provider wrappers.
 * App-specific pieces (claim create/delete/allocate, AI side, showActions) stay in wrappers.
 */
export const createStatementAdapter = (config: StatementAdapterConfig): StatementAdapter => {
    const { equalsFilter } = config;

    return {
        equalsFilter,
        patientStore: {
            loadPatientStatement: config.loadPatientStatement,
        },
        claimStore: config.claimStore,
        dialogContext: config.dialogContext,
        fetchProfessionalClaims: async (claimIds: string[]) => {
            return CoreFetch<ProfessionalApi>(ProfessionalApi).listProviderProfessionalClaims({
                filterClaimId: equalsFilter(claimIds),
            });
        },
        fetchClaimPayments: config.listClaimPayments,
        fetchClaimsByPatientId: async (patientId: string): Promise<ProviderClaim[]> => {
            return CoreFetch<ClaimApi>(ClaimApi).listProviderClaims({
                filterPatientId: equalsFilter([patientId]),
            });
        },
    };
};
